import type { Channel } from '@sbc/domain-models/gateway';

// Which credentials each channel actually reads. Both the connection form's inputs and the
// connection page's rows derive from this one map, because a screen that offers or reports a
// credential the channel cannot use is worse than a validation error: an "Access token — Not set"
// row on an `sbc` connection reads as broken configuration when in truth both of that channel's
// keys are process config. Keyed off the channel tuple so adding a channel forces the decision.
//
// Only Meta runs the GET webhook handshake, so only Meta has a verify token. The app secret is
// Meta's HMAC key, Intercom's (the platform Intercom app's client secret) and Slack's (the app's
// signing secret). `sbc` stores nothing: its ingress is authenticated by the platform-wide S2S
// token and its egress by SBC_ECOM_API_KEY. Intercom's actor is the admin authoring the note;
// Slack's is the app's bot user, which is how a mention of the agent is recognised. Only Slack
// holds an OAuth client: it is what lets a teammate grant the app their own token, so an
// `append-to-every-message` reply can be written under their message.
export interface ChannelCredentials {
  verifyToken: boolean;
  appSecret: boolean;
  accessToken: boolean;
  actor: boolean;
  oauthClient: boolean;
}

export const CHANNEL_CREDENTIALS: Record<Channel, ChannelCredentials> = {
  instagram: {
    verifyToken: true,
    appSecret: true,
    accessToken: true,
    actor: false,
    oauthClient: false,
  },
  facebook: {
    verifyToken: true,
    appSecret: true,
    accessToken: true,
    actor: false,
    oauthClient: false,
  },
  intercom: {
    verifyToken: false,
    appSecret: true,
    accessToken: true,
    actor: true,
    oauthClient: false,
  },
  slack: { verifyToken: false, appSecret: true, accessToken: true, actor: true, oauthClient: true },
  sbc: {
    verifyToken: false,
    appSecret: false,
    accessToken: false,
    actor: false,
    oauthClient: false,
  },
  tiktok: {
    verifyToken: false,
    appSecret: false,
    accessToken: false,
    actor: false,
    oauthClient: false,
  },
  facebook_group: {
    verifyToken: false,
    appSecret: false,
    accessToken: false,
    actor: true,
    oauthClient: false,
  },
  quo: {
    verifyToken: false,
    appSecret: true,
    accessToken: true,
    actor: false,
    oauthClient: false,
  },
  whatsapp: {
    verifyToken: true,
    appSecret: true,
    accessToken: true,
    actor: false,
    oauthClient: false,
  },
  klaviyo_sms: {
    verifyToken: false,
    appSecret: false,
    accessToken: true,
    actor: false,
    oauthClient: false,
  },
};

// What each credential is called on the channel's own side, so an operator can match a field to
// the page they copy it from. Shared by the form and the connection page for the same reason the
// map above is.
export interface CredentialLabels {
  account: string;
  actor: string;
  appSecret: string;
  accessToken: string;
}

const GENERIC_LABELS: CredentialLabels = {
  account: 'External account id',
  actor: 'External actor id',
  appSecret: 'App secret',
  accessToken: 'Access token',
};

export const CREDENTIAL_LABELS: Record<Channel, CredentialLabels> = {
  instagram: GENERIC_LABELS,
  facebook: GENERIC_LABELS,
  intercom: GENERIC_LABELS,
  slack: {
    account: 'Slack app id',
    actor: 'Bot user id',
    appSecret: 'Signing secret',
    accessToken: 'Bot token',
  },
  sbc: GENERIC_LABELS,
  tiktok: GENERIC_LABELS,
  facebook_group: { ...GENERIC_LABELS, account: 'Facebook group id', actor: 'Page id to post as' },
  quo: {
    ...GENERIC_LABELS,
    account: 'Quo phone number id',
    appSecret: 'Webhook signing key',
    accessToken: 'API key',
  },
  whatsapp: { ...GENERIC_LABELS, account: 'Phone number id', accessToken: 'System user token' },
  klaviyo_sms: {
    ...GENERIC_LABELS,
    account: 'Klaviyo public account id',
    accessToken: 'Private API key',
  },
};
